import { Inject, Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { SelectedPanelEnum } from '@app/core/enums/selected-panel.enum';
import { RequestService } from '@app/core/request/request.service';
import { WINDOW } from '@inshared/shared/util';
import { DirectControlButtonInterface } from '@app/my-zone/interfaces/direct-control-button.interface';
import { InsuranceModalService } from '@app/my-zone/services/insurance-modal.service';
import { ReplacementSalesfunnelService } from '@app/my-zone/shared/modals/replacement-salesfunnel/replacement-salesfunnel.service';
import { AppRoutesEnum } from '@app/routes/app-routes.enum';
import { SalesfunnelRoutes } from '@app/salesfunnel/routing/salesfunnel-routing.routes';
import { CompareTableControlService } from '@app/shared/services/compare-table-control.service';
import {
    PolicyValidityStatusCodeEnum,
    ProductCarResponseInterface,
    ProductMotorcycleResponseInterface,
    ProductRegularDriverResponseInterface,
    PurchasedProductInterface,
} from 'outshared-lib';
import { Observable, Subscription } from 'rxjs';
import { map, take, tap } from 'rxjs/operators';
import { DirectControlButtonActionModalEnum } from '../enums/direct-control-button-action-modal.enum';
import { DirectControlButtonIdEnum } from '../enums/direct-control-button-id.enum';
import { MyZoneRoutesEnum } from '../my-zone-routes.enum';
import { LocalStorageService } from '@inshared/shared/util-storage';

const LOCAL_STORAGE_KEY = 'ls.data:vervangen';
const START_DATE_UNKNOWN = '9999-12-31';

/** @deprecated - insurances functionality is moved to libs/my-zone/insurances */
@Injectable({
    providedIn: 'root',
})
export class DirectControlService implements OnDestroy {
    private subscriptions = new Subscription();
    public modalRef: any = null;

    constructor(
        private request: RequestService,
        private replacementSalesfunnelService: ReplacementSalesfunnelService,
        private insuranceModalService: InsuranceModalService,
        @Inject(WINDOW) private window: Window,
        @Inject(LocalStorageService) private localStorageService: Storage,
        private router: Router,
        private compareTableControlService: CompareTableControlService,
    ) {}

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    /** @deprecated use outshared-lib insurance-policy store */
    public downloadPolis(historyIndication: string): Observable<any> {
        const payload = {
            toon_historie_indicatie: historyIndication,
        };

        return this.request.download('relatie-algemeen/verstrekken/polis', payload);
    }

    public replaceCar(purchasedProduct: PurchasedProductInterface): void {
        const queryParams = `replace=true&purchased_product_sequence_number=${purchasedProduct.productSequenceNumber}`;
        const carLink = `/${SalesfunnelRoutes.CarInsurance}/${SalesfunnelRoutes.CalculateCarInsurance}?${queryParams}`;
        this.link(carLink);
    }

    public replaceMotorcycle(purchasedProduct: PurchasedProductInterface): void {
        const queryParams = `replace=true&purchased_product_sequence_number=${purchasedProduct.productSequenceNumber}`;
        const motorcycleLink = `/${SalesfunnelRoutes.MotorInsurance}/${SalesfunnelRoutes.CalculateMotorInsurance}?${queryParams}`;

        this.storeRegularDriverMotorcycle(purchasedProduct);
        this.link(motorcycleLink);
    }

    public initConfig(
        directControlButtons: DirectControlButtonInterface[],
        purchasedProduct: PurchasedProductInterface,
    ): DirectControlButtonInterface[] {
        const buttons: DirectControlButtonInterface[] = [];

        for (const directControlButton of directControlButtons) {
            const isDisabled = this.isDirectControlButtonDisabled(purchasedProduct, directControlButton);
            const isVisible = this.isDirectControlButtonVisible(purchasedProduct, directControlButton);
            const button: DirectControlButtonInterface = {
                ...directControlButton,
                productId: purchasedProduct.productId,
                validityStatusCode: purchasedProduct.product.validityStatusCode,
                disabled: isDisabled,
            };

            if (isVisible) {
                buttons.push(button);
            }
        }

        return buttons;
    }

    public modal(actionModal: DirectControlButtonActionModalEnum, purchasedProduct: PurchasedProductInterface): void {
        switch (actionModal) {
            case DirectControlButtonActionModalEnum.CancelAddedProduct: {
                this.insuranceModalService.cancelAddedProductModal(purchasedProduct);
                break;
            }
            case DirectControlButtonActionModalEnum.ReviveProduct: {
                this.insuranceModalService.reviveProductModal(purchasedProduct);
                break;
            }
            case DirectControlButtonActionModalEnum.NewCar:
                this.getReplacementCar(purchasedProduct);
                break;
            case DirectControlButtonActionModalEnum.NewMotor: {
                this.getReplacementMotorcycle(purchasedProduct);
                break;
            }
            case DirectControlButtonActionModalEnum.NewHome: {
                this.insuranceModalService.newHomeModal();
                break;
            }
        }
    }

    public togglePanel(selectedPanel: SelectedPanelEnum, purchasedProduct: PurchasedProductInterface): void {
        this.router.navigate([`/${AppRoutesEnum.MyZone}/${MyZoneRoutesEnum.MyInsurances}`], {
            queryParams: {
                productSequenceNumber: purchasedProduct.productSequenceNumber,
                productId: purchasedProduct.productId,
                panel: selectedPanel,
            },
        });
    }

    public routerLink(routerLink: string): void {
        this.router.navigate([routerLink]);
    }

    public link(link: string): void {
        this.window.location.href = link;
    }

    private isDirectControlButtonVisible(
        purchasedProduct: PurchasedProductInterface,
        directControlButton: DirectControlButtonInterface,
    ): boolean {
        if (
            directControlButton.id === DirectControlButtonIdEnum.InsuranceRegularDriver &&
            purchasedProduct?.productDetails?.regularDriverFuture
        ) {
            return false;
        }

        if (
            directControlButton.id === DirectControlButtonIdEnum.ChangeCoverage &&
            purchasedProduct?.coverage.coveragesAdjustmentPresentIndication
        ) {
            return false;
        }

        if (!directControlButton.productIds.includes(purchasedProduct.productId)) {
            return false;
        }

        if (!directControlButton.validityStatusCodes.includes(purchasedProduct.product.validityStatusCode)) {
            return false;
        }

        if (
            (directControlButton.id === DirectControlButtonIdEnum.InsuranceStartDateChange &&
                purchasedProduct.product.startDate === START_DATE_UNKNOWN) ||
            (directControlButton.id === DirectControlButtonIdEnum.InsuranceStartDateSet &&
                purchasedProduct.product.startDate !== START_DATE_UNKNOWN)
        ) {
            return false;
        }

        return true;
    }

    private isDirectControlButtonDisabled(
        purchasedProduct: PurchasedProductInterface,
        directControlButton: DirectControlButtonInterface,
    ): boolean {
        const isChangeDriverOrCoverage =
            directControlButton.id === DirectControlButtonIdEnum.InsuranceRegularDriver ||
            directControlButton.id === DirectControlButtonIdEnum.ChangeCoverage;
        const isButtonFutureOrTransfer =
            directControlButton.validityStatusCodes.includes(PolicyValidityStatusCodeEnum.Future) ||
            directControlButton.validityStatusCodes.includes(PolicyValidityStatusCodeEnum.Transfer);
        const isProductFutureOrTransfer =
            purchasedProduct.product.validityStatusCode === PolicyValidityStatusCodeEnum.Future ||
            purchasedProduct.product.validityStatusCode === PolicyValidityStatusCodeEnum.Transfer;
        const isButtonInternationalMotorInsuranceCard =
            directControlButton.id === DirectControlButtonIdEnum.InternationalMotorInsuranceCard;
        const isButtonTranser = directControlButton.validityStatusCodes.includes(PolicyValidityStatusCodeEnum.Transfer);
        const isProductTransfer = purchasedProduct.product.validityStatusCode === PolicyValidityStatusCodeEnum.Transfer;

        let isDisabled = false;

        if (isChangeDriverOrCoverage && isButtonFutureOrTransfer && isProductFutureOrTransfer) {
            isDisabled = true;
        }

        if (isButtonInternationalMotorInsuranceCard && isButtonTranser && isProductTransfer) {
            isDisabled = true;
        }

        return isDisabled;
    }

    private storeRegularDriverMotorcycle(purchasedProduct: PurchasedProductInterface): void {
        const productSequenceNumber = purchasedProduct.productSequenceNumber;
        const regularDriver = this.getRegularDriver(purchasedProduct);
        const vehicleDetails = this.getMotorcycleDetails(purchasedProduct);
        const vehicle = {
            ...vehicleDetails,
            productSequenceNumber,
            regularDriver,
        };
        const value = JSON.stringify(vehicle);

        this.localStorageService.removeItem(LOCAL_STORAGE_KEY);
        this.localStorageService.setItem(LOCAL_STORAGE_KEY, value);
    }

    private getRegularDriver(purchasedProduct: PurchasedProductInterface): ProductRegularDriverResponseInterface {
        return purchasedProduct?.productDetails?.regularDriver || purchasedProduct?.productDetails?.regularDriverFuture;
    }

    private getMotorcycleDetails(
        purchasedProduct: PurchasedProductInterface,
    ): ProductMotorcycleResponseInterface | ProductCarResponseInterface {
        return purchasedProduct.product.motorcycle;
    }

    private getReplacementCar(purchasedProduct: PurchasedProductInterface): void {
        this.getReplacementVehicles$(purchasedProduct)
            .pipe(take(1))
            .subscribe((result) => {
                if (!result.hasReplacementVehicles) {
                    this.link(`/${AppRoutesEnum.CarInsurance}/${SalesfunnelRoutes.CarInsurance}`);
                }
            });
    }

    private getReplacementMotorcycle(purchasedProduct: PurchasedProductInterface): void {
        this.getReplacementVehicles$(purchasedProduct)
            .pipe(take(1))
            .subscribe((result) => {
                if (!result.hasReplacementVehicles) {
                    this.link(`/${AppRoutesEnum.MotorInsurance}/${SalesfunnelRoutes.CalculateMotorInsurance}`);
                }
            });
    }

    private getReplacementVehicles$(
        purchasedProduct: PurchasedProductInterface,
    ): Observable<{ hasReplacementVehicles: boolean }> {
        return this.replacementSalesfunnelService.getReplacementVehicles$(purchasedProduct?.productId).pipe(
            map((products) => {
                if (products.purchased_product_replacement?.length) {
                    this.insuranceModalService.replaceVehicleModal(purchasedProduct);
                    return {
                        hasReplacementVehicles: true,
                    };
                }

                return {
                    hasReplacementVehicles: false,
                };
            }),
        );
    }
}
